import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  Divider,
  Paper,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  Collapse,
  Button,
  Alert,
} from "@mui/material";
import { Icon } from "@iconify/react";
import {
  calculateBookingPricePreview,
  formatSessionDateTimeRange,
  formatCurrency,
  getSortedSessions,
  resolveSessionId,
} from "./bookingPricingUtils";

const formatDate = (val) => {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};
const getPaymentMethodLabel = (method) => {
  if (method === "sepa") return "SEPA-Lastschrift";
  if (method === "bank_transfer") return "Überweisung";
  return "—";
};
const getPaymentSummaryHint = (method) => {
  if (method === "sepa") {
    return "Die Gebühr wird vom Inhaber des hinterlegten Vertrags per SEPA-Lastschrift eingezogen.";
  }
  if (method === "bank_transfer") {
    return "Nutzen Sie die untenstehenden Überweisungsdaten. Die Angaben erhalten Sie zusätzlich per E-Mail.";
  }
  return "";
};

export default function BookingStepSummary({
  event,
  participants,
  selectedSessionIds,
  selectedPricingRuleId,
  memberParticipantCount,
  bookingDate,
  pricingSnapshot,
  adminDiscount,
  consents,
  onConsentsChange,
  missingConsentIds,
  submitError,
  paymentMethod,
}) {
  const [expandedConsent, setExpandedConsent] = useState(null);
  const consentRefs = useRef({});
  const sortedSessions = useMemo(() => getSortedSessions(event?.sessions || []), [event]);
  const sessionById = useMemo(() => {
    const map = new Map();
    sortedSessions.forEach((session, index) => {
      map.set(resolveSessionId(session, `session-${index}`), session);
    });
    return map;
  }, [sortedSessions]);
  const pricePreview = useMemo(
    () =>
      calculateBookingPricePreview({
        event,
        selectedPricingRuleId,
        selectedSessionIds,
        participantCount: participants.length,
        memberParticipantCount,
        bookingDate,
        pricingSnapshot,
        adminDiscount,
      }),
    [
      adminDiscount,
      bookingDate,
      event,
      memberParticipantCount,
      participants.length,
      pricingSnapshot,
      selectedPricingRuleId,
      selectedSessionIds,
    ]
  );
  const pricingRule = pricePreview?.rule || null;
  const hasEarlyBirdDiscountConfigured =
    pricePreview &&
    (pricePreview.earlyBirdDiscountType === "fixed"
      ? pricePreview.earlyBirdDiscountFixedAmount > 0
      : pricePreview.earlyBirdDiscountPercent > 0);
  const displayedSessionIds =
    pricePreview?.rule?.type === "package" && (pricePreview?.effectiveSessionIds || []).length > 0
      ? pricePreview.effectiveSessionIds
      : selectedSessionIds;
  const sessions = displayedSessionIds
    .map((sessionId) => sessionById.get(String(sessionId || "")))
    .filter(Boolean);
  const requiredConsents = event?.registrationConfig?.requiredConsents || [];
  const paymentIsRequired = Boolean(pricePreview && Number(pricePreview.totalAmount) > 0);
  const paymentSummaryHint = getPaymentSummaryHint(paymentMethod);
  const bankTransferInfo = event?.registrationConfig?.bankTransferInfo || {};
  const bankTransferRecipient = String(bankTransferInfo?.recipient || "").trim();
  const bankTransferIban = String(bankTransferInfo?.iban || "").trim();
  const bankTransferBic = String(bankTransferInfo?.bic || "").trim();
  const bankTransferCustomText = String(bankTransferInfo?.customText || "").trim();
  const hasBankTransferAccountData = Boolean(
    bankTransferRecipient || bankTransferIban || bankTransferBic
  );

  const isConsentAccepted = (consentId) => consents.some((c) => c.consentId === consentId);
  const missingConsentSet = new Set(missingConsentIds || []);

  useEffect(() => {
    if (!Array.isArray(missingConsentIds) || missingConsentIds.length === 0) return;

    const firstMissingConsentId = missingConsentIds[0];
    const container = consentRefs.current[firstMissingConsentId];
    if (!container) return;

    container.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    const checkbox = container.querySelector('input[type="checkbox"]');
    checkbox?.focus();
  }, [missingConsentIds]);

  const toggleConsent = (consent) => {
    if (isConsentAccepted(consent.consentId)) {
      onConsentsChange(consents.filter((c) => c.consentId !== consent.consentId));
    } else {
      onConsentsChange([
        ...consents,
        {
          consentId: consent.consentId,
          title: consent.title,
          acceptedAt: new Date().toISOString(),
        },
      ]);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Zusammenfassung Ihrer Anmeldung
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Veranstaltung:</Typography>
            <Typography variant="body2" fontWeight={500}>{event?.eventName}</Typography>
          </Stack>
          {(event?.eventDateBegin || event?.eventDateEnd) && (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Zeitraum:</Typography>
              <Typography variant="body2">
                {formatDate(event.eventDateBegin)}
                {event.eventDateEnd && ` – ${formatDate(event.eventDateEnd)}`}
              </Typography>
            </Stack>
          )}
          {event?.locationName && (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Ort:</Typography>
              <Typography variant="body2">{event.locationName}</Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Teilnehmer */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Teilnehmer ({participants.length})
        </Typography>
        <Stack spacing={0.5}>
          {participants.map((p) => (
            <Typography key={p._key} variant="body2">
              • {p.firstName} {p.lastName}
            </Typography>
          ))}
        </Stack>
      </Paper>

      {/* Sessions */}
      {sessions.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Gewählte Tage ({sessions.length})
          </Typography>
          <Stack spacing={0.5}>
            {sessions.map((s, index) => {
              const sessionDateLabel = formatSessionDateTimeRange(s);
              return (
                <Typography
                  key={resolveSessionId(s, `summary-session-${index}`)}
                  variant="body2"
                >
                  • {s.label}
                  {sessionDateLabel ? ` · ${sessionDateLabel}` : ""}
                </Typography>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Preisübersicht */}
      {pricePreview && pricingRule && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            bgcolor: "rgba(25, 118, 210, 0.02)",
            borderColor: "primary.light",
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Preisübersicht
          </Typography>
          <Stack spacing={0.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {pricingRule.type === "package"
                  ? "Paket:"
                  : pricingRule.type === "per_session"
                  ? "Tagespreis:"
                  : "Tarif:"}
              </Typography>
              <Typography variant="body2">{pricingRule.label}</Typography>
            </Stack>
            {pricingRule.type === "per_session" && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Preis je Tag:</Typography>
                <Typography variant="body2">{formatCurrency(pricingRule.amount)}</Typography>
              </Stack>
            )}
            {pricingRule.type === "package" && (pricePreview.packageCount || 0) > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Paketpreis:
                </Typography>
                <Typography variant="body2">
                  {pricePreview.packageCount > 1
                    ? `${pricePreview.packageCount} x ${formatCurrency(pricePreview.packageUnitAmount)}`
                    : formatCurrency(pricePreview.packageUnitAmount)}
                </Typography>
              </Stack>
            )}
            {pricePreview.calculationError ? (
              <Alert severity="error" sx={{ mt: 0.5 }}>
                {pricePreview.calculationError}
              </Alert>
            ) : null}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Anzahl Teilnehmer:</Typography>
              <Typography variant="body2">{participants.length}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Zwischensumme:</Typography>
              <Typography variant="body2">{formatCurrency(pricePreview.grossAmount)}</Typography>
            </Stack>
            {pricePreview.memberDiscountAmount > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Mitgliederrabatt ({pricePreview.memberDiscountPercent}%):
                </Typography>
                <Typography variant="body2" color="success.main" fontWeight={500}>
                  -{formatCurrency(pricePreview.memberDiscountAmount)}
                </Typography>
              </Stack>
            )}
            {pricePreview.siblingDiscountAmount > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Geschwisterrabatt ({pricePreview.siblingDiscountPercent}%):
                </Typography>
                <Typography variant="body2" color="success.main" fontWeight={500}>
                  -{formatCurrency(pricePreview.siblingDiscountAmount)}
                </Typography>
              </Stack>
            )}
            {hasEarlyBirdDiscountConfigured && pricePreview.earlyBirdDiscountAmount > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Frühbucherrabatt:</Typography>
                <Typography variant="body2" color="success.main" fontWeight={500}>
                  -{formatCurrency(pricePreview.earlyBirdDiscountAmount)}
                </Typography>
              </Stack>
            )}
            {pricePreview.adminDiscountAmount > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {pricePreview.adminDiscountLabel || "Rabatt"}:
                </Typography>
                <Typography variant="body2" color="success.main" fontWeight={500}>
                  -{formatCurrency(pricePreview.adminDiscountAmount)}
                </Typography>
              </Stack>
            )}
            <Divider sx={{ my: 0.75 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body1" fontWeight={700}>
                Gesamtbetrag:
              </Typography>
              <Typography variant="body1" fontWeight={700} color="primary.main">
                {formatCurrency(pricePreview.totalAmount)}
              </Typography>
            </Stack>
            {paymentMethod ? (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Zahlungsart:</Typography>
                <Typography variant="body2">{getPaymentMethodLabel(paymentMethod)}</Typography>
              </Stack>
            ) : null}
            {paymentIsRequired && paymentSummaryHint ? (
              <Alert severity="info" sx={{ mt: 0.5 }}>
                {paymentSummaryHint}
              </Alert>
            ) : null}
            {paymentIsRequired && paymentMethod === "bank_transfer" ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  mt: 0.5,
                  bgcolor: "background.paper",
                }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    Überweisungsdaten
                  </Typography>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Betrag:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatCurrency(pricePreview.totalAmount)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      Verwendungszweck:
                    </Typography>
                    <Typography variant="body2" align="right">
                      Ihre Buchungsnummer (wird nach erfolgreicher Anmeldung erzeugt)
                    </Typography>
                  </Stack>
                  {bankTransferRecipient ? (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Empfänger:
                      </Typography>
                      <Typography variant="body2">{bankTransferRecipient}</Typography>
                    </Stack>
                  ) : null}
                  {bankTransferIban ? (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        IBAN:
                      </Typography>
                      <Typography variant="body2">{bankTransferIban}</Typography>
                    </Stack>
                  ) : null}
                  {bankTransferBic ? (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        BIC:
                      </Typography>
                      <Typography variant="body2">{bankTransferBic}</Typography>
                    </Stack>
                  ) : null}
                  {!hasBankTransferAccountData ? (
                    <Alert severity="warning" sx={{ mt: 0.5 }}>
                      Kontodaten sind aktuell nicht verfügbar. Nach der Anmeldung erhalten Sie die Zahlungsdaten per E-Mail.
                    </Alert>
                  ) : null}
                  {bankTransferCustomText ? (
                    <Alert severity="info" sx={{ mt: 0.5 }}>
                      {bankTransferCustomText}
                    </Alert>
                  ) : null}
                </Stack>
              </Paper>
            ) : null}
            {paymentIsRequired && !paymentMethod ? (
              <Alert severity="warning" sx={{ mt: 0.5 }}>
                Bitte wählen Sie vor dem Absenden eine Zahlungsart aus.
              </Alert>
            ) : null}
            {!paymentIsRequired ? (
              <Alert severity="success" sx={{ mt: 0.5 }}>
                Für diese Anmeldung ist keine Zahlung erforderlich.
              </Alert>
            ) : null}
          </Stack>
        </Paper>
      )}

      {/* Einwilligungen */}
      {requiredConsents.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Einwilligungen
          </Typography>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Bitte bestätigen Sie alle mit * markierten Pflicht-Einwilligungen.
          </Alert>
          <Stack spacing={1}>
            {requiredConsents
              .slice()
              .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
              .map((consent) => {
                const isMissing = missingConsentSet.has(consent.consentId);
                return (
                  <Paper
                    key={consent.consentId}
                    ref={(node) => {
                      if (node) {
                        consentRefs.current[consent.consentId] = node;
                      } else {
                        delete consentRefs.current[consent.consentId];
                      }
                    }}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderColor: isMissing ? "error.main" : undefined,
                    }}
                  >
                    <FormControl
                      required={consent.required}
                      error={isMissing}
                      component="fieldset"
                      variant="standard"
                      sx={{ width: "100%" }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isConsentAccepted(consent.consentId)}
                            onChange={() => toggleConsent(consent)}
                            required={consent.required}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {consent.title}
                              {consent.required && (
                                <Typography component="span" color="error"> *</Typography>
                              )}
                            </Typography>
                            {consent.text && (
                              <Button
                                size="small"
                                variant="text"
                                sx={{ p: 0, minWidth: 0, fontSize: "0.75rem" }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setExpandedConsent(
                                    expandedConsent === consent.consentId ? null : consent.consentId
                                  );
                                }}
                                endIcon={
                                  <Icon
                                    icon={
                                      expandedConsent === consent.consentId
                                        ? "mdi:chevron-up"
                                        : "mdi:chevron-down"
                                    }
                                    width={14}
                                  />
                                }
                              >
                                {expandedConsent === consent.consentId ? "Ausblenden" : "Volltext anzeigen"}
                              </Button>
                            )}
                            <Collapse in={expandedConsent === consent.consentId}>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block", whiteSpace: "pre-wrap" }}>
                                {consent.text}
                              </Typography>
                            </Collapse>
                          </Box>
                        }
                        sx={{ alignItems: "flex-start", m: 0 }}
                      />
                      {isMissing && (
                        <FormHelperText sx={{ mt: 0.5 }}>
                          Diese Einwilligung ist erforderlich.
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Paper>
                );
              })}
          </Stack>
        </Box>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitError}
        </Alert>
      )}
    </Box>
  );
}

BookingStepSummary.propTypes = {
  event: PropTypes.object.isRequired,
  participants: PropTypes.array.isRequired,
  selectedSessionIds: PropTypes.array.isRequired,
  selectedPricingRuleId: PropTypes.string,
  memberParticipantCount: PropTypes.number.isRequired,
  bookingDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  pricingSnapshot: PropTypes.object,
  adminDiscount: PropTypes.shape({
    label: PropTypes.string,
    type: PropTypes.oneOf(["percentage", "fixed"]),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  consents: PropTypes.array.isRequired,
  onConsentsChange: PropTypes.func.isRequired,
  missingConsentIds: PropTypes.array,
  submitError: PropTypes.string,
  paymentMethod: PropTypes.oneOf(["", "sepa", "bank_transfer"]),
};
